/**
 * Modal component for editing assignment quotas (allowance and remaining allowance)
 * Provides validation to ensure remaining allowance doesn't exceed total allowance
 */
<template>
  <va-modal
    v-model="state.isOpen"
    hide-default-actions
    :title="$t('assignments.editQuota.title')"
  >
    <va-form ref="form" @submit.prevent="handleSubmit">
      <!-- Allowance input field -->
      <va-input
        v-model.number="state.allowance"
        type="number"
        :label="$t('assignments.editQuota.allowance')"
        :hint="$t('assignments.editQuota.allowanceHint')"
        :rules="[validatePositiveNumber]"
        class="quota-input"
        @update:model-value="validateForm"
      />
      <!-- Remaining allowance input field with additional validation -->
      <va-input
        v-model.number="state.remainingAllowance"
        type="number"
        :label="$t('assignments.editQuota.remainingAllowance')"
        :hint="$t('assignments.editQuota.remainingAllowanceHint')"
        :rules="[validatePositiveNumber, validateRemainingAllowance]"
        class="quota-input"
        @update:model-value="validateForm"
      />
    </va-form>

    <!-- Modal footer with action buttons -->
    <template #footer>
      <div class="modal-footer">
        <va-button class="mr-2" @click="handleCancel">
          {{ $t('buttons.cancel') }}
        </va-button>
        <va-button 
          type="submit" 
          :loading="state.loading"
          :disabled="!isFormValid"
          @click="handleSubmit"
        >
          {{ $t('buttons.save') }}
        </va-button>
      </div>
    </template>
  </va-modal>
</template>

<script setup>
import { reactive, computed, inject, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { callPatchAssignment } from '@/api/licensing';

// Component props definition
const props = defineProps({
  isOpen: Boolean,
  assignmentId: String,
  initialAllowance: Number,
  initialRemainingAllowance: Number
});

// Component emits definition
const emit = defineEmits(['cancel', 'updated']);

// Global services and utilities
const { t } = useI18n();
const toasts = inject('toasts');
const handleError = inject('handleError');
const form = ref(null);

// Component state
const state = reactive({
  isOpen: computed(() => props.isOpen),
  allowance: props.initialAllowance,
  remainingAllowance: props.initialRemainingAllowance,
  loading: false
});

/**
 * Validation rules
 */
const validatePositiveNumber = (value) => value >= 0 || t('validation.positiveNumber');
const validateRemainingAllowance = (value) => 
  value <= state.allowance || t('assignments.editQuota.validation.remainingExceedsAllowance');

/**
 * Computed property to check form validity
 * Ensures both values are valid numbers, positive, and remaining allowance doesn't exceed total
 */
const isFormValid = computed(() => {
  const allowance = Number(state.allowance);
  const remaining = Number(state.remainingAllowance);
  
  return !Number.isNaN(allowance) && 
         !Number.isNaN(remaining) && 
         allowance >= 0 && 
         remaining >= 0 && 
         remaining <= allowance;
});

/**
 * Validates the form using Vuestic's form validation
 */
const validateForm = async () => {
  if (!form.value) {
    return;
  }
  await form.value.validate();
};

/**
 * Handles form submission
 * Updates the assignment quota if form is valid
 */
const handleSubmit = async () => {
  if (!isFormValid.value) {
    return;
  }
  
  state.loading = true;
  try {
    await callPatchAssignment(props.assignmentId, {
      usageQuota: {
        allowance: Number(state.allowance),
        remainingAllowance: Number(state.remainingAllowance)
      }
    });
    toasts?.info(t('assignments.editQuota.success'));
    emit('updated');
    emit('cancel');
  } catch (error) {
    handleError(error?.response?.data || error);
    toasts?.error(t('assignments.editQuota.error'));
  } finally {
    state.loading = false;
  }
};

/**
 * Handles modal cancellation
 */
const handleCancel = () => {
  emit('cancel');
};

// Watch for changes in allowance to trigger validation
watch(() => state.allowance, validateForm);

// Initial validation when modal opens
watch(() => state.isOpen, (isOpen) => {
  if (isOpen) {
    validateForm();
  }
});
</script>

<style scoped>
/* Input field styling */
.quota-input {
  width: 20rem;
  margin-bottom: 1.5rem;
  font-size: 1.1em;
}

/* Modal footer styling */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 1rem;
}
</style> 